// ─── What: the volume panel ───────────────────────────────────────────────────
//
// Bucketing lives in chartVolume.ts; this is the drawing and the hover.
//
// Note the two-phase shape. The volume panel is drawn BEFORE the crosshair
// elements exist (they are appended after the brush, so they sit above the
// plot), but its hover rect drives that same crosshair. In the original both
// lived in one closure and the forward reference resolved at event time. Here
// the rect is appended in place — preserving its z-order exactly — and
// `attachHover` binds the handlers once the chrome is available.

import * as d3 from 'd3';
import { alpha } from '@mui/material/styles';
import type { OHLCVPoint } from '../../../../api/types';
import { drawPanelSeparator } from '../../chartAxis';
import { buildVolumeBuckets, type VolumeSegment } from '../../chartVolume';
import { placeTooltip, buildVolTooltipHtml } from '../../chartTooltips';
import {
    outcomeLabel,
    OUTCOME_MAXLEN_CANVAS,
} from '../../../../utils/outcomeLabels';
import { MARGIN } from '../layout';
import { drawPanelToggle } from './panelChrome';
import type { ChartChrome, DrawCtx } from '../drawContext';

export interface VolumePanelDeps {
    noByMarket: ReadonlyMap<string, OHLCVPoint[]>;
    onToggle: () => void;
}

/** Binds the hover handlers once the shared crosshair exists. */
export type AttachVolumeHover = (chrome: ChartChrome) => void;

export function drawVolumePanel(
    ctx: DrawCtx,
    deps: VolumePanelDeps
): AttachVolumeHover {
    const {
        svg,
        layout,
        colors,
        theme,
        xScale,
        xDomain,
        series,
        resolution,
        colorMap,
        questionMap,
        outcomesMap,
        noSideColor,
        hasHighlight,
        highlightedMarkets,
        onMarketClick,
        tooltipRef,
        svgEl,
    } = ctx;
    const { w, volTop, volBottom, volHeight, volVisible } = layout;
    const { gridColor, textColor, sepColor, tc } = colors;

    drawPanelSeparator(svg, MARGIN.left, MARGIN.left + w, volTop - 1, sepColor);

    drawPanelToggle(svg, {
        top: volTop,
        centreX: MARGIN.left + w / 2,
        name: 'VOLUME',
        open: volVisible,
        textColor,
        onToggle: deps.onToggle,
    });

    if (!volVisible) return () => {};

    const resMs = resolution * 60 * 1000;
    const volBuckets = buildVolumeBuckets(
        series,
        deps.noByMarket,
        xDomain,
        resolution
    );

    let visibleMaxVol = 0;
    for (const bucket of volBuckets) {
        if (bucket.total > visibleMaxVol) visibleMaxVol = bucket.total;
    }
    if (visibleMaxVol === 0) visibleMaxVol = 1;

    const volYScale = d3
        .scaleLinear()
        .domain([0, visibleMaxVol])
        .range([volBottom, volTop])
        .nice();
    const volScale = d3
        .scaleLinear()
        .domain([0, visibleMaxVol])
        .range([0, volHeight - 4]);
    const volTicks = volYScale.ticks(3).filter(d => d > 0);

    svg.append('g')
        .selectAll('line')
        .data(volTicks)
        .join('line')
        .attr('x1', MARGIN.left)
        .attr('y1', d => volYScale(d))
        .attr('x2', MARGIN.left + w)
        .attr('y2', d => volYScale(d))
        .attr('stroke', gridColor)
        .attr('stroke-width', 0.5);
    svg.append('g')
        .selectAll('text')
        .data(volTicks)
        .join('text')
        .attr('x', MARGIN.left + w + 4)
        .attr('y', d => volYScale(d) + 3)
        .attr('fill', textColor)
        .attr('font-size', '8px')
        .attr('font-family', 'monospace')
        .attr('text-anchor', 'start')
        .text(d =>
            d >= 1e6
                ? `${(d / 1e6).toFixed(1)}M`
                : d >= 1e3
                  ? `${(d / 1e3).toFixed(0)}K`
                  : d.toFixed(0)
        );

    // Running total per stack identity (a market, or a side), so the tooltip can
    // show volume-to-date alongside the bucket's own.
    const sortedBucketKeys = volBuckets.map(b => b.bucketMs);
    const bucketByMs = new Map(volBuckets.map(b => [b.bucketMs, b]));
    const cumulativeVol = new Map<string, number>();
    const cumulativeAtBucket = new Map<number, Map<string, number>>();
    for (const bucket of volBuckets) {
        for (const seg of bucket.segments) {
            cumulativeVol.set(
                seg.key,
                (cumulativeVol.get(seg.key) ?? 0) + seg.vol
            );
        }
        cumulativeAtBucket.set(bucket.bucketMs, new Map(cumulativeVol));
    }

    // A segment's label and colour: markets keep their own line colour and
    // question; sides borrow the outcome names and the NO-line colour, so the
    // volume panel reads like the price panel above it.
    const segmentLabel = (seg: VolumeSegment): string => {
        if (seg.side === null) return questionMap.get(seg.conditionId) ?? '';
        const outcomes = outcomesMap.get(seg.conditionId) ?? null;
        const idx = seg.side === 'yes' ? 0 : 1;
        return (
            outcomeLabel(outcomes, idx, OUTCOME_MAXLEN_CANVAS)?.text ??
            (seg.side === 'yes' ? 'YES' : 'NO')
        );
    };
    const segmentColor = (seg: VolumeSegment): string => {
        if (seg.side === 'no')
            return noSideColor(outcomesMap.get(seg.conditionId) ?? null);
        return colorMap.get(seg.conditionId) ?? theme.palette.primary.main;
    };

    const barWidthPx = Math.max(
        1,
        Math.abs(xScale(new Date(resMs)) - xScale(new Date(0))) - 1
    );
    const volGroup = svg
        .append('g')
        .attr('clip-path', `url(#${layout.clipIds.vol})`);

    for (const bucket of volBuckets) {
        const cx = xScale(new Date(bucket.bucketMs));
        let cumY = 0;
        for (const entry of bucket.segments) {
            const h = volScale(entry.vol);
            const color = segmentColor(entry);
            const isHighlighted = !!(
                hasHighlight && highlightedMarkets!.has(entry.conditionId)
            );
            const isOther = !!(
                hasHighlight && !highlightedMarkets!.has(entry.conditionId)
            );
            volGroup
                .append('rect')
                .attr('class', 'vol-bar')
                .attr('data-vol-key', entry.key)
                .attr('x', cx - barWidthPx / 2)
                .attr('y', volBottom - cumY - h)
                .attr('width', barWidthPx)
                .attr('height', Math.max(0.5, h))
                .attr(
                    'fill',
                    alpha(color, isHighlighted ? 0.7 : isOther ? 0.15 : 0.35)
                )
                .attr('rx', 0.5)
                .style('cursor', 'pointer')
                .on('click', () => onMarketClick?.(entry.conditionId));
            cumY += h;
        }
    }

    // Appended here, not in attachHover, so its z-order matches the original.
    const hoverRect = svg
        .append('rect')
        .attr('x', MARGIN.left)
        .attr('y', volTop)
        .attr('width', w)
        .attr('height', volHeight)
        .attr('fill', 'transparent')
        .style('cursor', 'crosshair');

    return chrome => {
        hoverRect
            .on('mousemove', (event: MouseEvent) => {
                const [mx] = d3.pointer(event, svgEl);
                const hoverMs = xScale.invert(mx).getTime();
                let nearestKey = -1,
                    bestDist = Infinity;
                for (const bk of sortedBucketKeys) {
                    if (bk <= hoverMs) {
                        const dist = hoverMs - bk;
                        if (dist < bestDist) {
                            bestDist = dist;
                            nearestKey = bk;
                        }
                    }
                }
                if (nearestKey < 0 && sortedBucketKeys.length > 0)
                    nearestKey = sortedBucketKeys[0];
                if (nearestKey < 0) return;
                chrome.showCross(mx);
                placeTooltip(
                    tooltipRef.current,
                    event,
                    buildVolTooltipHtml(
                        bucketByMs.get(nearestKey)!.segments.map(seg => ({
                            label: segmentLabel(seg),
                            color: segmentColor(seg),
                            vol: seg.vol,
                            cum:
                                cumulativeAtBucket
                                    .get(nearestKey)!
                                    .get(seg.key) ?? 0,
                        })),
                        new Date(nearestKey),
                        tc
                    )
                );
            })
            .on('mouseleave', () => {
                chrome.hideCross();
                chrome.hideTooltip();
            });
    };
}
